/**
 * ApiKeysSettings Component
 *
 * Lets users create, view, and revoke API keys for connecting
 * external tools (e.g. Claude Code MCP server) to their Danny account.
 */

import { useState, useEffect, useCallback } from 'react';
import { Key, Plus, Trash2, Copy, Check, Loader2, AlertCircle, Terminal } from 'lucide-react';
import { fetchApi } from '../api/fetch';
import { btn } from './settings/buttonStyles';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ApiKey {
  id: string;
  label: string;
  prefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  createdAt: string;
}

interface CreatedApiKey extends ApiKey {
  key: string;
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function listApiKeys(): Promise<ApiKey[]> {
  return fetchApi<ApiKey[]>('/api-keys');
}

async function createApiKey(label: string): Promise<CreatedApiKey> {
  return fetchApi<CreatedApiKey>('/api-keys', {
    method: 'POST',
    body: JSON.stringify({ label }),
  });
}

async function revokeApiKey(id: string): Promise<void> {
  await fetchApi<unknown>(`/api-keys/${id}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ApiKeysSettings() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [copiedNewKey, setCopiedNewKey] = useState(false);
  const [copiedNewCommand, setCopiedNewCommand] = useState(false);
  const [copiedMcpCommand, setCopiedMcpCommand] = useState(false);
  const [copiedMcpConfig, setCopiedMcpConfig] = useState(false);

  // Revoke state
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  const loadKeys = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listApiKeys();
      setKeys(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load API keys';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadKeys();
  }, [loadKeys]);

  const handleCreate = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setIsCreating(true);
    setError(null);
    try {
      const result = await createApiKey(label);
      setCreatedKey(result);
      setNewLabel('');
      setShowCreateForm(false);
      // Reload list (new key will appear without the raw key value)
      await loadKeys();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create API key';
      setError(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyNewKey = async () => {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey.key);
    setCopiedNewKey(true);
    setTimeout(() => setCopiedNewKey(false), 2000);
  };

  const handleCopyNewCommand = async () => {
    if (!createdKey) return;
    const cmd = `claude mcp add danny --transport http --url https://api.askdanny.to/mcp --header "X-Api-Key: ${createdKey.key}"`;
    await navigator.clipboard.writeText(cmd);
    setCopiedNewCommand(true);
    setTimeout(() => setCopiedNewCommand(false), 2000);
  };

  const handleCopyMcpCommand = async () => {
    const cmd = `claude mcp add danny --transport http --url https://api.askdanny.to/mcp --header "X-Api-Key: your-key-here"`;
    await navigator.clipboard.writeText(cmd);
    setCopiedMcpCommand(true);
    setTimeout(() => setCopiedMcpCommand(false), 2000);
  };

  const handleCopyMcpConfig = async () => {
    await navigator.clipboard.writeText(mcpConfig);
    setCopiedMcpConfig(true);
    setTimeout(() => setCopiedMcpConfig(false), 2000);
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setIsRevoking(true);
    setError(null);
    try {
      await revokeApiKey(revokeTarget.id);
      setRevokeTarget(null);
      await loadKeys();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to revoke API key';
      setError(message);
    } finally {
      setIsRevoking(false);
    }
  };

  const mcpConfig = `{
  "mcpServers": {
    "danny": {
      "type": "http",
      "url": "https://api.askdanny.to/mcp",
      "headers": { "X-Api-Key": "your-key-here" }
    }
  }
}`;

  return (
    <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Key className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
            API Keys
          </h2>
        </div>
        {!showCreateForm && !createdKey && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-3 py-1.5 text-sm font-medium bg-primary-700 hover:bg-primary-800 text-white rounded-lg transition-colors flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" />
            Create API Key
          </button>
        )}
      </div>

      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
        Use API keys to connect external tools like Claude Code to your Danny account.
      </p>

      {/* Error banner */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Newly created key — show once */}
      {createdKey && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-300 dark:border-green-700 rounded-lg space-y-3">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
            <p className="text-sm font-semibold text-green-800 dark:text-green-200">
              API key created: <span className="font-bold">{createdKey.label}</span>
            </p>
          </div>
          <p className="text-xs text-green-700 dark:text-green-300 font-medium">
            Copy this key now — it won&apos;t be shown again.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-sm font-mono text-text-primary-light dark:text-text-primary-dark break-all">
              {createdKey.key}
            </code>
            <button
              onClick={handleCopyNewKey}
              className={`${btn.inline.sm} flex items-center gap-1.5`}
            >
              {copiedNewKey ? (
                <Check className="w-3.5 h-3.5 text-green-500" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
              {copiedNewKey ? 'Copied' : 'Copy'}
            </button>
          </div>
          {/* Connect Claude Code — ready-to-run command */}
          <div className="mt-1 p-3 bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg space-y-2">
            <div className="flex items-center gap-1.5">
              <Terminal className="w-3.5 h-3.5 text-text-secondary-light dark:text-text-secondary-dark flex-shrink-0" />
              <p className="text-xs font-semibold text-text-primary-light dark:text-text-primary-dark">
                Connect Claude Code — run this command:
              </p>
            </div>
            <div className="flex items-start gap-2">
              <code className="flex-1 px-3 py-2 bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-xs font-mono text-text-primary-light dark:text-text-primary-dark break-all">
                {`claude mcp add danny --transport http --url https://api.askdanny.to/mcp --header "X-Api-Key: ${createdKey.key}"`}
              </code>
              <button
                onClick={handleCopyNewCommand}
                className={`${btn.inline.sm} flex items-center gap-1.5`}
              >
                {copiedNewCommand ? (
                  <Check className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
                {copiedNewCommand ? 'Copied' : 'Copy command'}
              </button>
            </div>
          </div>
          <button
            onClick={() => setCreatedKey(null)}
            className="text-xs text-green-700 dark:text-green-400 hover:underline"
          >
            I&apos;ve saved it — dismiss
          </button>
        </div>
      )}

      {/* Create form */}
      {showCreateForm && (
        <div className="p-4 bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg space-y-3">
          <label className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
            Label
          </label>
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleCreate();
              if (e.key === 'Escape') { setShowCreateForm(false); setNewLabel(''); }
            }}
            placeholder="e.g. Claude Code — laptop"
            autoFocus
            className="w-full px-3 py-2 text-sm bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={handleCreate}
              disabled={isCreating || !newLabel.trim()}
              className="px-4 py-2 text-sm font-medium bg-primary-700 hover:bg-primary-800 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Creating…
                </>
              ) : (
                'Create'
              )}
            </button>
            <button
              onClick={() => { setShowCreateForm(false); setNewLabel(''); }}
              className="px-4 py-2 text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Keys table / list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
        </div>
      ) : keys.length === 0 ? (
        <p className="py-4 text-center text-sm text-text-secondary-light dark:text-text-secondary-dark italic">
          No API keys yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-text-secondary-light dark:text-text-secondary-dark border-b border-border-subtle-light dark:border-border-subtle-dark">
                <th className="pb-2 font-medium pr-3">Label</th>
                <th className="pb-2 font-medium pr-3">Prefix</th>
                <th className="pb-2 font-medium pr-3">Scopes</th>
                <th className="pb-2 font-medium pr-3">Last Used</th>
                <th className="pb-2 font-medium pr-3">Created</th>
                <th className="pb-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr
                  key={k.id}
                  className="border-b border-border-subtle-light dark:border-border-subtle-dark last:border-0"
                >
                  <td className="py-2.5 pr-3 font-medium text-text-primary-light dark:text-text-primary-dark">
                    {k.label}
                  </td>
                  <td className="py-2.5 pr-3 font-mono text-text-secondary-light dark:text-text-secondary-dark">
                    {k.prefix}…
                  </td>
                  <td className="py-2.5 pr-3 text-text-secondary-light dark:text-text-secondary-dark">
                    {k.scopes.join(', ') || '—'}
                  </td>
                  <td className="py-2.5 pr-3 text-text-secondary-light dark:text-text-secondary-dark tabular-nums">
                    {formatDate(k.lastUsedAt)}
                  </td>
                  <td className="py-2.5 pr-3 text-text-secondary-light dark:text-text-secondary-dark tabular-nums">
                    {formatDate(k.createdAt)}
                  </td>
                  <td className="py-2.5 text-right">
                    <button
                      onClick={() => setRevokeTarget(k)}
                      className="px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center gap-1 ml-auto"
                    >
                      <Trash2 className="w-3 h-3" />
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Revoke confirmation */}
      {revokeTarget && (
        <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm">
          <span className="text-red-800 dark:text-red-200">
            Revoke <strong>{revokeTarget.label}</strong>? This will immediately invalidate this key.
          </span>
          <div className="flex gap-2 flex-shrink-0 ml-3">
            <button
              onClick={() => setRevokeTarget(null)}
              disabled={isRevoking}
              className={btn.inline.sm}
            >
              Cancel
            </button>
            <button
              onClick={handleRevoke}
              disabled={isRevoking}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
            >
              {isRevoking ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              {isRevoking ? 'Revoking…' : 'Yes, revoke'}
            </button>
          </div>
        </div>
      )}

      {/* Claude Code config block */}
      <div className="pt-2 space-y-3">
        <div className="flex items-center gap-2">
          <Terminal className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" />
          <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
            Connect Claude Code
          </p>
        </div>

        {/* CLI command — primary */}
        <div className="space-y-1.5">
          <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
            Run this command with your API key (CLI — recommended):
          </p>
          <div className="flex items-start gap-2">
            <pre className="flex-1 px-3 py-2 bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-xs font-mono text-text-primary-light dark:text-text-primary-dark overflow-x-auto whitespace-pre">
{`claude mcp add danny --transport http \\
  --url https://api.askdanny.to/mcp \\
  --header "X-Api-Key: your-key-here"`}
            </pre>
            <button
              onClick={handleCopyMcpCommand}
              className={`${btn.inline.sm} flex items-center gap-1.5`}
            >
              {copiedMcpCommand ? (
                <Check className="w-3.5 h-3.5 text-green-500" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
              {copiedMcpCommand ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        {/* JSON config — fallback for Desktop/Web */}
        <div className="space-y-1.5">
          <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
            Or add this to your MCP settings manually (Desktop / Web):
          </p>
          <div className="flex items-start gap-2">
            <pre className="flex-1 text-xs font-mono bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-3 overflow-x-auto text-text-primary-light dark:text-text-primary-dark whitespace-pre">
              {mcpConfig}
            </pre>
            <button
              onClick={handleCopyMcpConfig}
              className={`${btn.inline.sm} flex items-center gap-1.5`}
            >
              {copiedMcpConfig ? (
                <Check className="w-3.5 h-3.5 text-green-500" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
              {copiedMcpConfig ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
